import { type ReactElement, type ReactNode, useRef } from 'react'
import {
  Flex,
  type FlexProps,
  Grid,
  GridItem,
  type LinkProps,
  useBreakpointValue,
  useStyleConfig,
  VStack
} from '@chakra-ui/react'
import GrandSection from 'ui/storefront/layout/GrandSection'
import GrandNextLink from 'ui/common/GrandNextLink'
import type {
  FooterItem,
  PaymentMethodLogosConfig,
  PaymentProviderName,
  SocialMediaItem
} from 'ecosystem'
import dynamic from 'next/dynamic'
import { useContrastTheme } from 'ui/hooks'
import { FooterColumn } from './FooterColumn'
import { FooterPaymentLogo } from './FooterPaymentLogo'
import { FooterGranditudeSign } from './FooterGranditudeSign'
import { FooterContextProvider } from './FooterContext'
import { type FooterStyleConfig, useFooterThemeStyles } from './footerTheme'

const FooterSocialMedia = dynamic(() => import('./FooterSocialMedia'), {
  ssr: false
})

export interface FooterStandardProps {
  logo: ReactElement
  footerItems: FooterItem[]
  socialMediaItems: SocialMediaItem<LinkProps>[]
  paymentProviderName?: PaymentProviderName
  paymentMethodLogos?: PaymentMethodLogosConfig
  /** Set `false` to drop the payment logos strip entirely (e.g. sites without checkout). */
  showPaymentLogo?: boolean
  maxColumns?: number
  socialMediasAfterAddon?: ReactNode
  paymentLogoContainerProps?: FlexProps
}

export const FooterStandard = ({
  logo,
  footerItems,
  socialMediaItems,
  paymentProviderName,
  paymentMethodLogos,
  showPaymentLogo = true,
  maxColumns: _maxColumns = 4,
  socialMediasAfterAddon,
  paymentLogoContainerProps
}: FooterStandardProps) => {
  const footerRef = useRef<HTMLDivElement>(null)
  const contrastColorTheme = useContrastTheme(footerRef.current)
  const maxColumns = Math.max(1, _maxColumns) // prevent of passing below zero
  const styleConfig = useStyleConfig('CustomFooter') as FooterStyleConfig
  const styles = useFooterThemeStyles(styleConfig)

  const socialDesktop = useBreakpointValue({
    base: 'none',
    md: 'initial'
  })

  const socialMobile = useBreakpointValue({
    base: 'initial',
    md: 'none'
  })

  return (
    <FooterContextProvider
      value={{
        contrastColorTheme
      }}
    >
      <GrandSection
        position="relative"
        sectionId="footer"
        contentContainerProps={{ spacing: 14 }}
        sx={styles}
        ref={footerRef}
      >
        <Flex justifyContent="center" w="100%">
          <GrandNextLink aria-label="home" href="/">
            {logo}
          </GrandNextLink>
        </Flex>

        <VStack alignItems="center" w="full" gap={4}>
          <Grid
            w="100%"
            gap={4}
            templateColumns={{
              base: '1fr',
              md: `repeat(${maxColumns}, 1fr)`
            }}
          >
            {footerItems.slice(0, maxColumns - 1).map((item, index) => (
              <GridItem key={index} rowSpan={1}>
                <FooterColumn {...{ item }} />
              </GridItem>
            ))}

            <GridItem display={socialDesktop} rowSpan={2}>
              <VStack align="center" gap={2}>
                <FooterSocialMedia align="center" items={socialMediaItems} />
                {socialMediasAfterAddon}
              </VStack>
            </GridItem>

            {footerItems.slice(maxColumns - 1).map((item, index) => (
              <GridItem key={index} rowSpan={1}>
                <FooterColumn {...{ item }} />
              </GridItem>
            ))}

            <GridItem display={socialMobile} rowSpan={2}>
              <VStack gap={2} w="full">
                <FooterSocialMedia align="flex-start" items={socialMediaItems} />
                {socialMediasAfterAddon}
              </VStack>
            </GridItem>
          </Grid>

          {showPaymentLogo ? (
            <Flex justifyContent="center" py={12} {...paymentLogoContainerProps}>
              <FooterPaymentLogo
                methodLogos={paymentMethodLogos}
                paymentProviderName={paymentProviderName}
              />
            </Flex>
          ) : null}

          <FooterGranditudeSign />
        </VStack>
      </GrandSection>
    </FooterContextProvider>
  )
}
